import { useCallback, useEffect, useRef } from 'react';
import { api } from '../util/api';

/** POST target, relative to the API origin (see util/api.ts). */
export const BOOKING_DRAFT_ENDPOINT = 'api/booking-draft';
/** Blur events inside this window collapse into one POST. */
export const BOOKING_DRAFT_DEBOUNCE_MS = 400;
/** The same draft content is never posted twice within this window. */
export const BOOKING_DRAFT_THROTTLE_MS = 30_000;

export interface BookingDraftAddress {
  street?: string;
  city?: string;
  province?: string;
  provinceCode?: string;
  postalCode?: string;
  country?: string;
  countryCode?: string;
  lat?: number;
  lng?: number;
}

export interface BookingDraftVehicle {
  year?: string;
  make?: string;
  model?: string;
  engine?: string;
  vin?: string;
}

export interface BookingDraftItem {
  id?: number;
  name?: string;
  nameKey?: string;
}

/**
 * Exactly what the server accepts:
 * server/src/modules/booking-draft/dto/booking-draft.dto.ts
 */
export interface BookingDraftPayload {
  email: string;
  name?: string;
  phone?: string;
  address?: BookingDraftAddress;
  vehicle?: BookingDraftVehicle;
  services?: BookingDraftItem[];
  addOns?: BookingDraftItem[];
  bookingType?: 'plan' | 'one-time';
  scheduledAt?: string;
  lang?: string;
  deviceId?: string;
}

/** Same rule the Schedule step uses to enable "Lock in my booking". */
export const isDraftEmailValid = (value?: string | null): boolean => (
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim())
);

/** Drops undefined / empty values so the server's whitelist never trips. */
export function compactDraft(
  payload: BookingDraftPayload,
): BookingDraftPayload {
  const out: Record<string, unknown> = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value) && value.length === 0) return;
    if (typeof value === 'object' && !Array.isArray(value)) {
      const inner: Record<string, unknown> = {};
      Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') inner[k] = v;
      });
      if (Object.keys(inner).length === 0) return;
      out[key] = inner;
      return;
    }
    out[key] = value;
  });
  return out as unknown as BookingDraftPayload;
}

/**
 * Abandoned-booking capture for the schedule step.
 *
 * `capture()` is called when the email field blurs. It is debounced, only
 * posts when the email is valid, and never posts the same content twice
 * within BOOKING_DRAFT_THROTTLE_MS. Nothing at all happens - no timer, no
 * request - unless `enabled` is true, which is the tenant's `captureDrafts`
 * flag; Go Oil and every other tenant without it are untouched.
 *
 * Failures are swallowed: this is a best-effort signal, never part of the
 * booking flow.
 */
export function useBookingDraftCapture(
  enabled: boolean,
  build: () => BookingDraftPayload | null,
) {
  const buildRef = useRef(build);
  buildRef.current = build;
  const timer = useRef<number | null>(null);
  const last = useRef<{ signature: string; at: number } | null>(null);

  const send = useCallback(() => {
    if (!enabled) return;
    const built = buildRef.current();
    if (!built || !isDraftEmailValid(built.email)) return;
    const payload = compactDraft({ ...built, email: built.email.trim() });
    const signature = JSON.stringify(payload);
    const now = Date.now();
    if (
      last.current
      && last.current.signature === signature
      && now - last.current.at < BOOKING_DRAFT_THROTTLE_MS
    ) {
      return;
    }
    last.current = { signature, at: now };
    api.post(BOOKING_DRAFT_ENDPOINT, payload).catch(() => {
      // best effort: a lost draft is not a lost booking
    });
  }, [enabled]);

  const capture = useCallback(() => {
    if (!enabled) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(send, BOOKING_DRAFT_DEBOUNCE_MS);
  }, [enabled, send]);

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  const hasCaptured = useCallback(() => last.current !== null, []);

  return { capture, hasCaptured };
}
